/**
 * @module app.um
 */

import { autoinject, singleton } from 'aurelia-framework';
import { ComponentBase, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { AuthService, IAuthSearchFilters } from "app/um/services/auth-service";
import { IMemberSearchFilters } from 'app/cd/services/member-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { AuthSearchFormCustomElement } from 'app/um/components/common/auth-search-form';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { IAuthSearchOptions } from 'app/um/components/auth/auth-filters';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { EssForm, FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { authIsReadOnly } from 'app/um/lib/utilities';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { SaveAuth } from 'app/um/components/auth/save-auth';
import { requiredFields as memberRequiredFields, requiredPlanFields as planRequiredFields } from 'app/cd/elements/members/member-display';
import * as UM from 'app/um/models/essette-api';

@singleton()
@autoinject()
export class AuthSearchPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    protected authSearchFilters: IAuthSearchFilters = {}
    protected tempAuthSearchFilters: IAuthSearchFilters | null = null;
    protected authSearchOptions: Partial<IAuthSearchOptions> = {}
    protected memberSearchFilters: IMemberSearchFilters = {};
    protected tempMemberSearchFilters: IMemberSearchFilters | null = null;
    protected authResults: UM.Auth[] = [];
    protected $isValid!: boolean;
    protected validateForm = validate;
    protected editedAuth: UM.Auth | null = null;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;

    protected pageSize: number = 20;

    protected hasAuthOwner: boolean = false;
    protected hasAuthRoute: boolean = false;
    protected hasCreateAuthRoute: boolean = false;
    private hasEditAuthDetails: boolean = false;
    private hasEditAuth: boolean = false;
    private hasEditClosedAuth: boolean = false;
    protected showAuthSummaryLink: boolean = false;
    protected openAuthSummary = openAuthSummary;

    protected showProviderSummaryLink: boolean = false;
    protected openProviderSummary = openProviderSummary;

    protected authAvailableFilters = [
        'mine', 'openOnly', 'closedOnly', 'overDue', 'number', 'type', 'class', 'class.subclass', 'status',
        'status.reason', 'assignedTo', 'requestedDateTime', 'nextReviewDate', 'dueDate', 'providerType', 'providerLastName'
    ];
    protected memberAvailableFilters: string[] | null = [
        'active', 'identifier.primary', 'identifier.medicaid', 'name.first', 'name.last', 'birthDate',
        'primaryCarePhysician', 'primaryCarePhysician.name.first', 'primaryCarePhysician.name.last',
        'healthPlan', 'lineOfBusiness'
    ];

    ////****************** Element References ***************************************////
    protected $authSearch!: AuthSearchFormCustomElement;
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $editAuthForm!: EssForm;
    protected $vmSaveAuth!: SaveAuth;


    ////****************** Event Subscriptions **************************************////


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        //private readonly context: ManageAuthsPageContext,
        private readonly service: AuthService,
        protected transitional: TransitionalManager,
        ...rest: any[]) {

        super(...rest);
    }

    protected async canActivate(): Promise<boolean> {
        await this.checkPermissions();

        return true;
    }

    protected activate(): void {
        this.clearResults();
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ***********************************************////
    private async checkPermissions(): Promise<void> {
        this.clearFilters();

        await this.accountContext.load();
        this.hasAuthOwner = this.accountContext.hasPermission('UM.Auth.Duty.Owner');
        this.hasCreateAuthRoute = this.accountContext.hasRouteAccess('um.manage-auths.create');
        this.hasEditAuthDetails = this.accountContext.hasPermission('UM.Auth.Update.EditAuthDetails');
        this.hasEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        this.hasEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.showAuthSummaryLink = this.accountContext.hasRouteAccess('um.auth.summary');
        this.hasAuthRoute = this.accountContext.hasRouteAccess('um.auth');
        this.authSearchOptions.mine = { isVisible: this.hasAuthOwner, isLocked: false };
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
    }

    protected canEditAuth(auth: UM.Auth): boolean {
        if(!auth) { return false; }
        const readonly = authIsReadOnly(auth, this.env.user.id, this.hasEditAuth, this.hasEditClosedAuth);
        return !readonly && this.hasEditAuthDetails;
    }

    protected editAuth(auth: UM.Auth): void {
        if(this.canEditAuth(auth)) {
            this.$vmSaveAuth.configureAuth(auth);
            this.$modal.open();
        }
    }

    protected editClose(): void {
        this.$modal.close();
        this.$vmSaveAuth.clearAuthContext();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        if(detail.changes && (<UM.Auth>detail.changes).id) {
            this.editedAuth = <UM.Auth>detail.changes;
        }
        if(this.$modal) {
            this.$modal.close();

            this.searchAuths();
        }
    }

    /** Handles the grid's sort event */
    protected async applySort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        // only trigger search if there is data to sort
        if(this.authResults.length) await this.searchAuths();
    }

    protected clearResults() {
        this.authResults = [];
        this.resultCount = 0;
        if(this.$pager) this.$pager.updatePage(0);
    }

    protected clearFilters() {
        this.authSearchFilters = {};
        this.tempAuthSearchFilters = {};
        this.memberSearchFilters = {};
        this.tempMemberSearchFilters = {};
    }

    protected submitAuthSearch() {
        this.authSearchFilters = { ...this.tempAuthSearchFilters };
        this.memberSearchFilters = { ...this.tempMemberSearchFilters };
        this.searchAuths();
    }

    protected submitPagedSearch() {
        this.searchAuths();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async searchAuths(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            page: this.$pager?.page || 0,
            top: this.pageSize,
            sorting: this.sorting,
            includeInactive: true
        };

        const includes: string[] = [
            'assignedTo',
            'class',
            'class.subClass',
            'custom',
            'custom.target',
            'healthcareServiceType',
            'member',
            'member.plan',
            'member.plan.healthPlan',
            'member.plan.lineOfBusiness',
            'member.primaryCarePhysician',
            'priority',
            'provider.attending',
            'provider.requested',
            'provider.requesting',
            'provider.servicing',
            'placeOfService',
            'status',
            'status.reason',
            'type'
        ];

        const fields: string[] = [
            'assignedTo.description',
            'class.description',
            'class.subClass.description',
            'custom.lookup.*',
            'custom.text.*',
            'alternateNumber',
            'dueDateTime',
            'healthcareServiceType.description',
            'member.birthDate',
            'member.description',
            'member.isUnknown',
            'member.plan.eligibility.isCurrent',
            'member.plan.healthPlan.description',
            'member.plan.lineOfBusiness.description',
            'member.primaryCarePhysician.description',
            'member.primaryCarePhysician.identifier.primary',
            'member.primaryCarePhysician.name.full',
            'nextReviewDate',
            'number',
            'placeOfService.description',
            'priority.description',
            'provider.attending.description',
            'provider.attending.name.full',
            'provider.requested.description',
            'provider.requested.name.full',
            'provider.requesting.description',
            'provider.requesting.name.full',
            'provider.servicing.description',
            'provider.servicing.name.full',
            'requestedDateTime',
            'status.description',
            'status.reason.description',
            'type.description'
        ];

        memberRequiredFields.forEach(field => !fields.includes(`member.${field}`) ? fields.push(`member.${field}`) : null);
        planRequiredFields.forEach(field => !fields.includes(`member.plan.${field}`) ? fields.push(`member.plan.${field}`) : null);

        try {
            this.state.beginLoad();

            // too many filters can exceed URL character limit, so use filters only to fetch IDs first
            const idResponse = await this.service.searchAuths(this.authSearchFilters, this.memberSearchFilters, options, [], ['id'], true);
            const authIds: string[] = idResponse.results.map(auth => auth.id!);
            const fullResponse = await this.service.loadAuthsById(authIds, { sorting: this.sorting, includeInactive: true }, includes, fields);
            this.authResults = fullResponse.results;
            this.resultCount = idResponse.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }
}
